<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { debounce } from "lodash-es";
import { useList } from "~/composables/use-list";
import FormSelect, { type SelectOption } from "@artech-ui/components/ui/form/FormSelect.vue";
import FormTagSelect from "@artech-ui/components/ui/form/FormTagSelect.vue";

// Workspace picker backed by GET /workspaces?keyword=. Single select by default,
// `multiple` for allow lists. Mirrors artech-client's selects/UserSelect.
interface Workspace {
  id: string;
  name: string;
}

interface Props {
  modelValue?: string | string[];
  label?: string;
  helpText?: string;
  error?: string;
  placeholder?: string;
  size?: "default" | "sm";
  disabled?: boolean;
  multiple?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: "",
  size: "default",
  disabled: false,
  multiple: false,
});

const emit = defineEmits<{
  (e: "update:modelValue", value: string | string[]): void;
}>();

const { t } = useI18n();
const { $api } = useNuxtApp();

const { items, filter, load } = useList<Workspace, { keyword: string; page: number; pageSize: number }>({
  endpoint: "/workspaces",
  defaultFilter: () => ({ keyword: "", page: 1, pageSize: 50 }),
});

// Already-selected workspaces stay labelled even when the current search page
// no longer contains them.
const known = ref(new Map<string, string>());
watch(items, () => {
  for (const w of items.value) known.value.set(w.id, w.name);
});

const selectedIDs = computed(() =>
  (Array.isArray(props.modelValue) ? props.modelValue : [props.modelValue]).filter(Boolean),
);

const options = computed<SelectOption[]>(() => {
  const seen = new Set<string>();
  const result: SelectOption[] = [];
  for (const id of [...selectedIDs.value, ...items.value.map((w) => w.id)]) {
    if (seen.has(id)) continue;
    seen.add(id);
    result.push({ value: id, label: known.value.get(id) || id });
  }
  return result;
});

const search = debounce((query: string) => {
  filter.keyword = query;
  filter.page = 1;
  load();
}, 250);

// Ids that arrive before any search (an app being edited) get their names.
async function resolveSelected() {
  const missing = selectedIDs.value.filter((id) => !known.value.has(id));
  if (!missing.length) return;
  try {
    const resp = await $api<{ items: Workspace[] }>("/workspaces", { query: { ids: missing.join(","), pageSize: missing.length } });
    for (const w of resp.items ?? []) known.value.set(w.id, w.name);
  } catch {
    // Labels fall back to the id.
  }
}

watch(selectedIDs, resolveSelected);

onMounted(async () => {
  await load();
  await resolveSelected();
});
</script>

<template>
  <component
    :is="multiple ? FormTagSelect : FormSelect"
    :model-value="modelValue"
    :options="options"
    :label="label"
    :help-text="helpText"
    :error="error"
    :placeholder="placeholder || t('ui.search')"
    :size="size"
    :disabled="disabled"
    :multiple="multiple"
    searchable
    :search-placeholder="t('ui.search')"
    :on-search="search"
    @update:model-value="emit('update:modelValue', $event ?? (multiple ? [] : ''))"
  />
</template>
